import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { JackpotDetails } from '@smartico/public-api/dist/Jackpots'
import { AchRelatedGame } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import { Smartico } from '../../core/Smartico'
import { RootState } from '../store'

export interface JackpotDetailsExtended extends JackpotDetails {
    related_games?: AchRelatedGame[]
}

interface JackpotState {
    jackpots: JackpotDetailsExtended[]
    error: string | null
}

const initialState: JackpotState = {
    jackpots: [],
    error: null,
}

export const fetchJackpots = createAsyncThunk('jackpot/fetchJackpots', async (_, { rejectWithValue }) => {
    try {
        const smarticoApi = await Smartico.apiReady
        if (smarticoApi && smarticoApi.jackpotGet) {
            const jackpots = await smarticoApi.jackpotGet()
            return jackpots
        } else {
            return rejectWithValue('Smartico API or jackpotGet method not available.')
        }
    } catch (error) {
        return rejectWithValue('Failed to fetch jackpots')
    }
})

const jackpotSlice = createSlice({
    name: 'jackpot',
    initialState,
    reducers: {
        setJackpots(state, action: PayloadAction<JackpotDetailsExtended[]>) {
            state.jackpots = action.payload
        },
        updateJackpots(state, action: PayloadAction<JackpotDetailsExtended[]>) {
            state.jackpots = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchJackpots.pending, (state) => {
                state.jackpots = []
                state.error = null
            })
            .addCase(fetchJackpots.fulfilled, (state, action) => {
                state.jackpots = action.payload
                state.error = null
            })
            .addCase(fetchJackpots.rejected, (state, action) => {
                state.jackpots = []
                state.error = action.payload as string
            })
    },
})

export const { setJackpots, updateJackpots } = jackpotSlice.actions

export const selectAllJackpots = (state: RootState) => state.jackpot.jackpots

export const selectJackpotsByGameId = (gameId: string) => (state: RootState) => {
    return state.jackpot.jackpots.filter(jackpot =>
        jackpot.related_games?.some(game => game.ext_game_id === gameId)
    )
}

export default jackpotSlice.reducer